import { getToken } from '@api/config';
import { logoutUser } from '@helpers/auth.helper';
import { AxiosInstance, AxiosResponse } from 'axios';
import createApiClient, { handleTokenRefresh, isValidToken } from './httpHandler';
import { HttpStatusCode } from './httpStatusCodeConfig';

interface FetchOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}

class FetchWrapper {
  private apiClient: AxiosInstance;

  private mainApiClient: AxiosInstance | null = null;

  private mainApiBase = process.env.REACT_APP_API_URL || '';

  private actionPlanBase = process.env.REACT_APP_ACTION_PLAN_API_URL || '';

  constructor(baseURL = process.env.REACT_APP_ACTION_PLAN_API_URL) {
    this.apiClient = createApiClient(baseURL);

    if (this.mainApiBase && this.mainApiBase !== baseURL) {
      this.mainApiClient = createApiClient(this.mainApiBase);
      this.addFetchCompatibleInterceptor(this.mainApiClient);
    }

    this.addFetchCompatibleInterceptor(this.apiClient);
  }

  private getClientForRequest(config: { baseURL?: string }): AxiosInstance {
    if (this.mainApiClient && config.baseURL === this.mainApiBase) {
      return this.mainApiClient;
    }

    return this.apiClient;
  }

  private addFetchCompatibleInterceptor(client: AxiosInstance): void {
    client.interceptors.response.clear();
    client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const originalRequest = error.config;
        const clientForRetry = this.getClientForRequest(originalRequest);

        const currentToken = getToken();
        const shouldRefreshToken =
          (error.response?.status === HttpStatusCode.UNAUTHORIZED || !isValidToken(currentToken)) &&
          !originalRequest.retry;

        if (shouldRefreshToken) {
          originalRequest.retry = true;

          try {
            const newToken = await handleTokenRefresh();

            if (!newToken && !isValidToken(newToken)) {
              logoutUser();
            }

            if (newToken && isValidToken(newToken)) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;

              return clientForRetry(originalRequest);
            }
          } catch (refreshError) {
            return Promise.resolve({
              status: HttpStatusCode.UNAUTHORIZED,
              statusText: 'Unauthorized',
              data: { error: 'Token refresh failed' },
              headers: {},
              config: originalRequest,
            });
          }
        }

        if (error.response?.status === HttpStatusCode.FORBIDDEN) {
          logoutUser();
        }

        return Promise.resolve({
          ...error.response,
          status: error.response?.status || 500,
          statusText: error.response?.statusText || 'Network Error',
          data: error.response?.data || { error: error.message },
        });
      },
    );
  }

  async fetch(url: string, options: any = {}): Promise<Response> {
    const { method = 'GET', headers = {}, body } = options;

    let client = this.apiClient;
    let requestUrl = url;

    if (this.mainApiClient && this.mainApiBase && url.startsWith(this.mainApiBase)) {
      client = this.mainApiClient;
      requestUrl = url.slice(this.mainApiBase.length) || '/';
    } else if (this.actionPlanBase && url.startsWith(this.actionPlanBase)) {
      client = this.apiClient;
      requestUrl = url.slice(this.actionPlanBase.length) || '/';
    }

    if (!requestUrl.startsWith('/')) {
      requestUrl = `/${requestUrl}`;
    }

    try {
      const response = await client.request({
        url: requestUrl,
        method,
        headers,
        data: body ? JSON.parse(body) : undefined,
      });

      // Create a proper Response object using the Response constructor
      const responseBody = JSON.stringify(response.data);
      const responseInit: ResponseInit = {
        status: response.status,
        statusText: response.statusText,
        headers: new Headers(response.headers as Record<string, string>),
      };

      // Create a Response object
      const fetchResponse = new Response(responseBody, responseInit);

      // Add additional properties that fetch Response has
      Object.defineProperties(fetchResponse, {
        type: {
          value: 'cors' as ResponseType,
          writable: false,
          enumerable: true,
          configurable: false,
        },
        url: {
          value: response.config?.url || url,
          writable: false,
          enumerable: true,
          configurable: false,
        },
        redirected: {
          value: false,
          writable: false,
          enumerable: true,
          configurable: false,
        },
      });

      return fetchResponse;
    } catch (error: any) {
      console.error('FetchWrapper error:', error);
      // Create an error Response object
      const errorBody = JSON.stringify({
        error: error.message || 'Unknown error',
        status: error.response?.status || 500,
      });

      const errorResponse = new Response(errorBody, {
        status: error.response?.status || 500,
        statusText: error.response?.statusText || 'Internal Server Error',
        headers: new Headers({ 'Content-Type': 'application/json' }),
      });

      Object.defineProperties(errorResponse, {
        type: {
          value: 'cors' as ResponseType,
          writable: false,
          enumerable: true,
          configurable: false,
        },
        url: {
          value: url,
          writable: false,
          enumerable: true,
          configurable: false,
        },
        redirected: {
          value: false,
          writable: false,
          enumerable: true,
          configurable: false,
        },
      });

      return errorResponse;
    }
  }
}

// Create a global instance
const fetchWrapper = new FetchWrapper();

// Export the fetch method to replace native fetch
export const axiosFetchHandler = fetchWrapper.fetch.bind(fetchWrapper);

// Export class for creating instances with different configurations
export { FetchWrapper };
